/**
 * Canvas toolbar — the single consolidated control surface for tools,
 * vision-mode settings, and per-selection editing. Replaces what used
 * to live across the canvas IconButton row plus the GMControls panel
 * in the right sidebar.
 *
 * Layout (top-left of canvas, stacks vertically):
 *   - Tool row (always): ICON-ONLY SegmentedToggle of Select / Ruler /
 *     (Barrier + Fog for GM — both always present, no vision-mode
 *     gating). Accessible names ride along as visually-hidden labels +
 *     title tooltips. One mutually-exclusive radio group, animated pill.
 *   - Fog sub-strip (when toolMode === 'fog', GM): dynamic-LoS on/off toggle
 *     (eye / eye-slash, wired to `losEnabled`), reveal / hide direction
 *     toggles, "Hide all" / "All visible" bulk buttons, brush-size slider.
 *     Fog and dynamic LoS are the two vision controls, grouped here.
 *   - Barrier sub-strip (when toolMode === 'barrier', GM): the "Show barriers
 *     to players" switch plus placement help text.
 *   - Selected-barrier popover (when selectedBarrierId is set):
 *     blocks-vision / blocks-movement Switches for the chosen barrier
 *     — replaces the dedicated GM-tab selected-barrier section.
 */

import { useState } from 'react'
import { Button } from '@arcanecircle/ui/grimoire/button'
import { IconButton } from '@arcanecircle/ui/grimoire/icon-button'
import { Slider } from '@arcanecircle/ui/ui/slider'
import { Switch } from '@arcanecircle/ui/ui/switch'
import { SegmentedToggle } from '@arcanecircle/ui/ui/segmented-toggle'
import { useVTTStore } from '@/store'
import { socketService } from '@/services/socket'
import type { ToolMode, MeasurementShape } from '@/types'

/** Accessible name for an icon-only control — visually hidden, read by AT. */
function VisuallyHidden({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        position: 'absolute',
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        whiteSpace: 'nowrap',
        border: 0,
      }}
    >
      {children}
    </span>
  )
}

export interface ToolbarProps {
  /** Keyboard hint shown in the Ruler button tooltip. */
  rulerKeyHint?: string
  /** Drives which tools render. GM gets barrier / fog / vision-mode
   *  controls; players see only the cross-role set. */
  userRole?: 'gm' | 'player' | 'observer'
}

type ToolOption = { value: ToolMode; label: React.ReactNode; icon: React.ReactNode }

const SELECT_ICON = (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 2l7 19 2.5-7.5L20 11z" />
  </svg>
)
const RULER_ICON = (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 17l6-6 4 4 6-6 2 2" />
    <path d="M5 21h14" />
  </svg>
)
const BARRIER_ICON = (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
  </svg>
)
// Reveal fog = sun (let the light in / clear). Deliberately NOT an eye — the
// eye belongs to the dynamic-LoS toggle, and reusing it here made the two
// controls read as duplicates.
const FOG_ICON = (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
  </svg>
)
// Hide fog = cloud (cover the map). Distinct from both the eye and the sun.
const FOG_HIDE_ICON = (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
  </svg>
)
// Light-bulb for the dynamic lighting tool.
const LIGHT_ICON = (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 21h6" />
    <path d="M12 3C8.69 3 6 5.69 6 9c0 2.12 1.07 3.98 2.7 5.1.4.28.7.72.7 1.2V17h6v-1.7c0-.48.3-.92.7-1.2C17.93 12.98 19 11.12 19 9c0-3.31-2.69-6-7-6z" />
  </svg>
)
// Eye / eye-slash for the dynamic-LoS toggle (on = eye, off = eye-slash).
const EYE_ICON = (
  <svg
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)
const EYE_SLASH_ICON = (
  <svg
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
    <line x1="2" x2="22" y1="2" y2="22" />
  </svg>
)

export function Toolbar({ rulerKeyHint = 'R', userRole }: ToolbarProps) {
  const {
    toolMode,
    setToolMode,
    fogMode,
    setFogMode,
    measurementShape,
    setMeasurementShape,
    brushSize,
    setBrushSize,
    getActiveMap,
    setLosEnabled,
    setShowBarriers,
    setRenderMode,
    setLightingEnabled,
    getActiveMapBarriers,
    selectedBarrierId,
    updateBarrier,
  } = useVTTStore()
  const map = getActiveMap()
  const losEnabled = map?.losEnabled === true
  const showBarriers = map?.showBarriers !== false
  const lightingEnabled = map?.lightingEnabled === true
  // Default darkness so a fresh map hides content from players out of the box.
  const renderMode = map?.renderMode ?? 'darkness'
  const isGM = userRole === 'gm'
  const barriers = getActiveMapBarriers()
  const selectedBarrier = selectedBarrierId
    ? barriers.find((b) => b.id === selectedBarrierId)
    : null

  // Tool row is ICONS ONLY — accessible names ride along as visually-hidden
  // labels (+ the title tooltip). Players see Select + Ruler. The GM ALWAYS
  // gets both Barrier and Fog now; neither is gated by a vision mode.
  const toolOptions: ToolOption[] = [
    {
      value: 'select',
      label: <VisuallyHidden>Select</VisuallyHidden>,
      icon: <span title="Select" aria-hidden>{SELECT_ICON}</span>,
    },
    {
      value: 'ruler',
      label: <VisuallyHidden>{`Ruler (${rulerKeyHint})`}</VisuallyHidden>,
      icon: <span title={`Ruler (${rulerKeyHint})`} aria-hidden>{RULER_ICON}</span>,
    },
  ]
  if (isGM && map) {
    toolOptions.push({
      value: 'barrier',
      label: <VisuallyHidden>Barrier</VisuallyHidden>,
      icon: <span title="Barrier" aria-hidden>{BARRIER_ICON}</span>,
    })
    toolOptions.push({
      value: 'fog',
      label: <VisuallyHidden>Fog</VisuallyHidden>,
      icon: <span title="Fog" aria-hidden>{FOG_ICON}</span>,
    })
    toolOptions.push({
      value: 'light',
      label: <VisuallyHidden>Light</VisuallyHidden>,
      icon: <span title="Light sources" aria-hidden>{LIGHT_ICON}</span>,
    })
  }

  // Measurement-template shapes for the ruler tool. Available to every
  // role that can use the ruler (players + GM). `line` is the classic
  // straight ruler; cone/circle/square are 5e area templates.
  const shapeOptions: { value: MeasurementShape; label: string }[] = [
    { value: 'line', label: 'Line' },
    { value: 'cone', label: 'Cone' },
    { value: 'circle', label: 'Circle' },
    { value: 'square', label: 'Square' },
  ]

  const handleLosToggle = () => {
    const next = !losEnabled
    setLosEnabled(next)
    if (map) {
      try {
        socketService.updateMapVision(map.id, next, undefined)
      } catch {
        // socket not connected — local change stands, session-state
        // replay on reconnect reconciles
      }
    }
  }

  const handleRenderModeToggle = () => {
    const next = renderMode === 'darkness' ? 'dimmed' : 'darkness'
    setRenderMode(next)
    if (map) {
      try {
        socketService.updateMapVision(map.id, undefined, undefined, undefined, next)
      } catch {
        // socket not connected — local change stands, session-state replay reconciles
      }
    }
  }

  const handleBulkFog = (revealed: boolean) => {
    if (!map) return
    try {
      socketService.setAllFog(map.id, revealed)
    } catch {
      // socket not connected — server broadcast on reconnect reconciles
    }
  }

  const handleLightingToggle = () => {
    const next = !lightingEnabled
    setLightingEnabled(next)
    if (map) {
      try {
        socketService.updateMapVision(map.id, undefined, undefined, next)
      } catch {
        // socket not connected — local change stands, session-state replay reconciles
      }
    }
  }

  const handleShowBarriersChange = (show: boolean) => {
    setShowBarriers(show)
    if (map) {
      try {
        socketService.updateMapVision(map.id, undefined, show)
      } catch {
        // see above
      }
    }
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: 12,
        left: 12,
        zIndex: 30,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        alignItems: 'flex-start',
        maxWidth: 320,
      }}
    >
      {/* Tool selector — animated SegmentedToggle, icons only. Players get
          Select / Ruler; GM additionally gets Barrier + Fog (both always). */}
      <SegmentedToggle<ToolMode>
        ariaLabel="Tool"
        value={toolMode}
        onChange={setToolMode}
        options={toolOptions}
      />

      {/* Ruler shape sub-strip — pick the measurement template (line /
          cone / circle / square) while the Ruler tool is active. Wires
          straight to the store; useRuler/RulerOverlay read the same
          `measurementShape`. */}
      {toolMode === 'ruler' && (
        <div
          className="ac-card"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            padding: '0.5rem 0.625rem',
            minWidth: 220,
          }}
        >
          <label
            className="ac-help"
            style={{
              fontSize: '0.6875rem',
              display: 'block',
            }}
          >
            Template shape
          </label>
          <SegmentedToggle<MeasurementShape>
            ariaLabel="Measurement shape"
            value={measurementShape}
            onChange={setMeasurementShape}
            options={shapeOptions}
          />
        </div>
      )}

      {/* Fog sub-strip — dynamic-LoS toggle + fog direction, brush size, and
          bulk ops, only while the Fog tool is active. Fog and dynamic LoS are
          the two vision controls, so the LoS toggle lives here rather than
          squatting the toolbar permanently. The GM fog client-gate still
          applies via the tool's presence. */}
      {toolMode === 'fog' && isGM && (
        <div
          className="ac-card"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            padding: '0.5rem 0.625rem',
            minWidth: 220,
          }}
        >
          {/* Dynamic line-of-sight on/off (eye / eye-slash). */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <span style={{ fontSize: '0.75rem' }}>Dynamic line-of-sight</span>
            <IconButton
              size="sm"
              variant={losEnabled ? 'primary' : 'ghost'}
              title={losEnabled ? 'Dynamic LoS on — click to disable' : 'Dynamic LoS off — click to enable'}
              aria-label="Toggle dynamic line-of-sight"
              aria-pressed={losEnabled}
              onClick={handleLosToggle}
            >
              {losEnabled ? EYE_ICON : EYE_SLASH_ICON}
            </IconButton>
          </div>
          {/* Fog render mode — how unrevealed fog looks to players. Darkness
              (default) fully hides content; Dimmed leaves terrain faintly
              visible. GM authoring view is unaffected. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <span style={{ fontSize: '0.75rem' }}>Fog hides content</span>
            <Button
              type="button"
              variant={renderMode === 'darkness' ? 'primary' : 'outline'}
              size="sm"
              title={
                renderMode === 'darkness'
                  ? 'Darkness — players see solid black over fog (click for dimmed)'
                  : 'Dimmed — players see terrain faintly through fog (click for darkness)'
              }
              aria-pressed={renderMode === 'darkness'}
              onClick={handleRenderModeToggle}
            >
              {renderMode === 'darkness' ? 'Darkness' : 'Dimmed'}
            </Button>
          </div>
          <div className="ac-tool-cluster">
            <IconButton
              size="sm"
              variant={fogMode === 'reveal' ? 'primary' : 'ghost'}
              title="Reveal fog"
              aria-label="Reveal fog"
              aria-pressed={fogMode === 'reveal'}
              onClick={() => setFogMode('reveal')}
            >
              {FOG_ICON}
            </IconButton>
            <IconButton
              size="sm"
              variant={fogMode === 'hide' ? 'primary' : 'ghost'}
              title="Hide fog"
              aria-label="Hide fog"
              aria-pressed={fogMode === 'hide'}
              onClick={() => setFogMode('hide')}
            >
              {FOG_HIDE_ICON}
            </IconButton>
          </div>
          {/* Bulk fog ops — cover/clear the whole active map at once.
              Grimoire Button (outline) so hover/active/focus states match the
              rest of the design system instead of a bare styled <button>. */}
          <div style={{ display: 'flex', gap: 6 }}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              title="Cover the whole map in fog"
              onClick={() => handleBulkFog(false)}
              style={{ flex: 1 }}
            >
              Hide all
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              title="Clear all fog on the map"
              onClick={() => handleBulkFog(true)}
              style={{ flex: 1 }}
            >
              All visible
            </Button>
          </div>
          <div>
            <label
              className="ac-help"
              style={{
                fontSize: '0.6875rem',
                display: 'block',
                marginBottom: 4,
              }}
            >
              Brush size · {brushSize} squares
            </label>
            <Slider
              min={1}
              max={5}
              value={[brushSize]}
              onValueChange={([v]) => setBrushSize(v)}
            />
          </div>
        </div>
      )}

      {/* Barrier sub-strip — the "show barriers to players" switch (moved here
          from the permanent toolbar row, since it's barrier-scoped) plus the
          placement hint. Only while the Barrier tool is active. */}
      {toolMode === 'barrier' && isGM && (
        <div
          className="ac-card"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            padding: '0.5rem 0.625rem',
            minWidth: 220,
            fontSize: '0.75rem',
          }}
        >
          <Switch
            checked={showBarriers}
            onCheckedChange={handleShowBarriersChange}
          >
            <span style={{ fontSize: '0.75rem' }}>Show barriers to players</span>
          </Switch>
          <span style={{ color: 'hsl(var(--ac-text-muted))' }}>
            Left-click adds vertices. Right-click finishes the shape.
            Press <kbd className="kbd">B</kbd> to toggle,{' '}
            <kbd className="kbd">Esc</kbd> to cancel. Minimum 3 vertices.
          </span>
        </div>
      )}

      {/* Light tool sub-strip — dynamic lighting toggle + placement hint.
          Only visible to GM when the Light tool is active. */}
      {toolMode === 'light' && isGM && (
        <div
          className="ac-card"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            padding: '0.5rem 0.625rem',
            minWidth: 220,
            fontSize: '0.75rem',
          }}
        >
          <Switch
            checked={lightingEnabled}
            onCheckedChange={handleLightingToggle}
          >
            <span style={{ fontSize: '0.75rem' }}>Dynamic lighting</span>
          </Switch>
          <span style={{ color: 'hsl(var(--ac-text-muted))' }}>
            Left-click to place a light source. Light sources respect walls.
            Players toggle torches from the token tooltip.
          </span>
        </div>
      )}

      {/* Selected-barrier contextual popover. Floats below the toolbar
          when a barrier is selected; carries the flag toggles that used
          to live in the GM tab. */}
      {isGM && selectedBarrier && (
        <SelectedBarrierCard
          barrierId={selectedBarrier.id}
          vertexCount={selectedBarrier.points.length}
          blocksVision={selectedBarrier.blocksVision !== false}
          blocksMovement={selectedBarrier.blocksMovement === true}
          onVisionChange={(v) => {
            updateBarrier(selectedBarrier.id, { blocksVision: v })
            try {
              socketService.updateBarrier(selectedBarrier.id, { blocksVision: v })
            } catch {
              // see above
            }
          }}
          onMovementChange={(v) => {
            updateBarrier(selectedBarrier.id, { blocksMovement: v })
            try {
              socketService.updateBarrier(selectedBarrier.id, { blocksMovement: v })
            } catch {
              // see above
            }
          }}
        />
      )}
    </div>
  )
}

function SelectedBarrierCard({
  vertexCount,
  blocksVision,
  blocksMovement,
  onVisionChange,
  onMovementChange,
}: {
  barrierId: string
  vertexCount: number
  blocksVision: boolean
  blocksMovement: boolean
  onVisionChange: (v: boolean) => void
  onMovementChange: (v: boolean) => void
}) {
  // Local "open" toggle. Keeps the popover from squatting the toolbar
  // when the GM doesn't want to see it. Defaults open since the user
  // just selected the barrier.
  const [open, setOpen] = useState(true)
  return (
    <div
      className="ac-card"
      role="region"
      aria-label="Selected barrier"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '0.5rem 0.625rem',
        minWidth: 220,
        borderColor: 'hsl(var(--ac-mint-primary) / 0.5)',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: 0,
          background: 'transparent',
          border: 'none',
          color: 'inherit',
          cursor: 'pointer',
          font: 'inherit',
          textAlign: 'left',
        }}
      >
        <span
          className="ac-panel-eyebrow"
          style={{
            borderTop: 'none',
            paddingTop: 0,
            color: 'hsl(var(--ac-mint-primary))',
          }}
        >
          Selected barrier · {vertexCount} vertices
        </span>
        <svg
          aria-hidden
          width={10}
          height={10}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            color: 'hsl(var(--ac-text-muted))',
            transition: 'transform 150ms var(--ac-ease-out, ease-out)',
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <>
          <Switch checked={blocksVision} onCheckedChange={onVisionChange}>
            <span style={{ fontSize: '0.75rem' }}>Blocks vision</span>
          </Switch>
          <Switch checked={blocksMovement} onCheckedChange={onMovementChange}>
            <span style={{ fontSize: '0.75rem' }}>Blocks movement</span>
          </Switch>
          <span
            className="ac-help"
            style={{
              fontSize: '0.6875rem',
              color: 'hsl(var(--ac-text-muted))',
            }}
          >
            Press <kbd className="kbd">Delete</kbd> to remove.
          </span>
        </>
      )}
    </div>
  )
}
